import React, { memo, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { Link } from 'react-router-dom'

import { Button } from '../button'
import Text from '../../../src/ui/Text'
import { WidgetLayout } from '../widget-layout'
import { SDangerButton } from '../partner-admins-widget'
import { AddCompanyChargerModal } from '../add-company-charger-modal'
import { ChargersPagination } from '../chargers-pagination'
import { Table, TableBody, TableBodyCell, TableHeader, TableHeaderCell, TableRow } from '../table'
import { ChargePoint, useCompanyRemoveChargePointMutation } from '../../common/redux/api/web-api'
import { notifyError, notifySuccess } from '../../common/features/notificationsManager/Notify'
import useText from '../../common/hooks/useText'

export type CompanyChargersWidgetProps = {
    chargePoints: Array<ChargePoint>
    loadingStatus: boolean
    companyId?: string
    className?: ClassName
    style?: React.CSSProperties
    paginationData: { totalCount: number; pageIndex: number; pageSize: number }
    setPageIndex: (index: number) => void
}

export const CompanyChargersWidget = memo(function CompanyChargersWidget({
    chargePoints,
    loadingStatus,
    companyId,
    className,
    style,
    paginationData,
    setPageIndex,
}: CompanyChargersWidgetProps): React.ReactElement {
    const text = useText()
    const [removeChargePointFromCompany, { isLoading }] = useCompanyRemoveChargePointMutation()
    const [isAddCompanyChargerModalOpened, setIsAddCompanyChargerModalOpened] = useState(false)

    const handleRemoveChargePoint = (chargePointId: string) => {
        if (!companyId) {
            notifyError(text('msgCouldNotRemoveCharger'))
            return
        }
        removeChargePointFromCompany({ companyId, chargePointId })
            .unwrap()
            .then(() => notifySuccess(text('msgChargerRemovedFromOrg', { chargePointId })))
            .catch(() => notifyError(text('msgCouldNotRemoveCharger')))
    }

    return (
        <WidgetLayout
            className={cn(className)}
            style={style}
            headerButtons={[
                <Button
                    small
                    title={text('btnAdd')}
                    onClick={() => {
                        setIsAddCompanyChargerModalOpened(true)
                    }}
                    data-testid="btn-add-charger"
                />,
            ]}
            title={text('lblChargersWidget')}
            data-testid="company-chargers-table"
        >
            {isAddCompanyChargerModalOpened && (
                <AddCompanyChargerModal
                    close={() => {
                        setIsAddCompanyChargerModalOpened(false)
                    }}
                    cancel={() => setIsAddCompanyChargerModalOpened(false)}
                    companyId={companyId}
                />
            )}
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell data-testid="table-th-serial">
                            <Text code="lblSerialNumber" />
                        </TableHeaderCell>
                        <TableHeaderCell data-testid="table-th-name">
                            <Text code="lblName" />
                        </TableHeaderCell>
                        <TableHeaderCell />
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {chargePoints?.map((chargePoint) => (
                        <TableRow key={Math.random()}>
                            <TableBodyCell data-testid={'table-td-serial-' + chargePoint.id}>
                                <Link
                                    to={`/chargepoints/${chargePoint?.id}`}
                                    className="btn btn-link btn-xs"
                                    data-testid="table-chargePoint-name"
                                >
                                    {chargePoint.id}
                                </Link>
                            </TableBodyCell>
                            <TableBodyCell data-testid={'table-td-name-' + chargePoint.id}>
                                {chargePoint.name}
                            </TableBodyCell>
                            <TableBodyCell data-testid={'table-td-remove-' + chargePoint.id}>
                                <SDangerButton
                                    dataTestId={`danger-button-${chargePoint.id}`}
                                    tooltip={
                                        !!chargePoint.loadBalancerGroupId || isLoading
                                            ? text('msgRemoveChargerTooltip')
                                            : text('lblRemove')
                                    }
                                    onClick={() => handleRemoveChargePoint(chargePoint.id)}
                                    title={<span className="fa fa-remove" />}
                                    disabled={!!chargePoint.loadBalancerGroupId || isLoading}
                                    small
                                />
                            </TableBodyCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {chargePoints?.length > 0 && (
                <ChargersPagination
                    data-testid="pagination-chargers"
                    totalCount={paginationData?.totalCount}
                    pageIndex={paginationData?.pageIndex}
                    pageSize={paginationData?.pageSize}
                    displayedChargers={chargePoints?.length}
                    setPageIndex={(index) => setPageIndex(index)}
                    showButtons={false}
                />
            )}
            {(loadingStatus || isLoading) && (
                <div className="shimmer-container shimmer-small" data-testid="chargers-loading"></div>
            )}
        </WidgetLayout>
    )
})
